import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { gsap } from '@/lib/gsap'
import { useBooking } from '@/components/landing/booking-provider'
import Magnetic from '@/components/landing/magnetic'
import { INQUIRY_COPY } from '@/constants/aerive'

const Inquiries: FC = (): ReactNode => {
    const { openBooking } = useBooking()
    const sectionRef = useRef<HTMLElement>(null)

    useEffect(() => {
        const section = sectionRef.current
        if (!section) return

        const mm = gsap.matchMedia()

        mm.add({
            motion: '(prefers-reduced-motion: no-preference)',
            reduce: '(prefers-reduced-motion: reduce)'
        }, (context) => {
            const { motion } = context.conditions as { motion: boolean }

            section.querySelectorAll<HTMLElement>('[data-reveal]').forEach((element) => {
                gsap.fromTo(
                    element,
                    motion ? { autoAlpha: 0, y: 60, filter: 'blur(8px)' } : { autoAlpha: 0 },
                    {
                        autoAlpha: 1,
                        y: 0,
                        filter: 'blur(0px)',
                        ease: 'power2.out',
                        scrollTrigger: {
                            trigger: element,
                            start: 'top 94%',
                            end: 'top 66%',
                            scrub: 1,
                            refreshPriority: 4
                        }
                    }
                )
            })
        })

        return () => mm.revert()
    }, [])

    return (
        <section
            id='inquiries'
            ref={sectionRef}
            aria-labelledby='inquiries-title'
            className='relative z-10 bg-obsidian px-5 pb-28 pt-20 text-ivory md:px-[6vw] md:pb-40 md:pt-28'
        >
            <div data-reveal className='flex items-center justify-between border-t border-champagne/20 pt-5'>
                <p className='text-[11px] uppercase tracking-[0.25em] text-champagne'>{INQUIRY_COPY.eyebrow}</p>
                <p className='text-[11px] uppercase tracking-[0.25em] text-smoke'>Replies are personal</p>
            </div>

            <div className='mt-14 grid gap-12 md:mt-20 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] lg:items-end lg:gap-20'>
                <h2 id='inquiries-title' data-reveal className='font-heading text-[clamp(3rem,7.2vw,7.8rem)] font-light leading-[0.9] tracking-[-0.015em]'>
                    {INQUIRY_COPY.title[0]}
                    <br />
                    <span className='text-champagne'>{INQUIRY_COPY.title[1]}</span>
                </h2>

                <div data-reveal>
                    <p className='max-w-md text-[15px] leading-relaxed text-ivory/70'>
                        {INQUIRY_COPY.text}
                    </p>
                    <Magnetic className='mt-9'>
                        <button
                            type='button'
                            onClick={openBooking}
                            className='inline-flex h-14 items-center gap-3 rounded-full bg-champagne px-8 text-[12px] uppercase tracking-[0.22em] text-obsidian transition-colors hover:bg-champagne-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-champagne'
                        >
                            Book a flight
                            <ArrowUpRight className='size-4' />
                        </button>
                    </Magnetic>
                </div>
            </div>
        </section>
    )
}

export default Inquiries
